import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

/**
 * A single avatar in an `app-avatar-group`. `initials` is optional — when omitted it is derived
 * from `label`. `color` tints the circle (falls back to the brand colour when null).
 */
export interface AvatarGroupItem {
  id: string;
  label: string;
  initials?: string;
  color?: string | null;
}

/**
 * Overlapping circular initial avatars with a "+N" overflow chip — the standard way to summarise a
 * collection of small entities (teams, tags, members, assignees) in a grid cell or detail panel.
 *
 * ```html
 * <app-avatar-group
 *   [items]="row.teamChips" [max]="3" [clickable]="true"
 *   (itemClick)="goToTeam($event)"
 * />
 * ```
 *
 * Set `clickable` to make each avatar a button (cursor + hover lift) that emits `itemClick`; the
 * component stops propagation so the click never reaches a surrounding row/cell handler.
 */
@Component({
  selector: 'app-avatar-group',
  standalone: true,
  imports: [NgbTooltipModule],
  template: `
    @if (items.length) {
      <div class="avatar-group">
        @for (item of visible; track item.id) {
          <span
            class="avatar-group-item"
            [class.clickable]="clickable"
            [style.width.px]="size"
            [style.height.px]="size"
            [style.font-size.px]="fontSize"
            [style.background]="item.color || null"
           [ngbTooltip]="item.label"
           container="body"
           placement="top auto"
            (click)="onItemClick(item, $event)"
            >{{ initials(item) }}</span
          >
        }
        @if (overflow > 0) {
          <span
            class="avatar-group-item avatar-group-more"
            [style.width.px]="size"
            [style.height.px]="size"
            [style.font-size.px]="fontSize"
             [ngbTooltip]="items.length + ' total'"
             container="body"
             placement="top auto"
            >+{{ overflow }}</span
          >
        }
      </div>
    } @else {
      <span class="avatar-group-empty">{{ emptyText }}</span>
    }
  `,
  styles: [
    `
      .avatar-group {
        display: inline-flex;
        align-items: center;
      }
      .avatar-group-item {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: -8px;
        border-radius: 50%;
        border: 2px solid #fff;
        background: var(--h4c-dark-blue, #262262);
        color: #fff;
        font-weight: 700;
        line-height: 1;
      }
      .avatar-group-item:first-child {
        margin-left: 0;
      }
      .avatar-group-item.clickable {
        cursor: pointer;
        transition: transform 0.12s ease, box-shadow 0.12s ease;
      }
      .avatar-group-item.clickable:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(15, 23, 42, 0.2);
        z-index: 2;
      }
      .avatar-group-more {
        background: var(--bg-muted, #f1f5f9);
        color: var(--fg-2, #64748b);
        cursor: default;
      }
      .avatar-group-empty {
        color: var(--fg-4, #94a3b8);
        font-size: 12.5px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarGroupComponent {
  @Input() items: AvatarGroupItem[] = [];
  /** How many avatars to show before collapsing the rest into a "+N" chip. */
  @Input() max = 3;
  /** Diameter of each avatar in pixels. */
  @Input() size = 28;
  /** When true each avatar is a clickable button that emits `itemClick`. */
  @Input() clickable = false;
  /** Text shown when there are no items. */
  @Input() emptyText = '—';

  @Output() itemClick = new EventEmitter<AvatarGroupItem>();

  get visible(): AvatarGroupItem[] {
    return (this.items ?? []).slice(0, this.max);
  }

  get overflow(): number {
    return Math.max(0, (this.items?.length ?? 0) - this.max);
  }

  get fontSize(): number {
    return Math.round(this.size * 0.38);
  }

  initials(item: AvatarGroupItem): string {
    if (item.initials) return item.initials;
    const parts = (item.label ?? '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  onItemClick(item: AvatarGroupItem, event: MouseEvent): void {
    if (!this.clickable) return;
    event.stopPropagation();
    this.itemClick.emit(item);
  }
}
